/**
 * connections (Festipod glue) — the app owns the relationship concept.
 *
 * "Connected" is a Festipod domain fact (an accepted, two-sided friendship), not
 * something the data SDK models: the SDK models reading as key POSSESSION, and
 * giving someone that key is ONE act — `inbox.share(doc, toUser)`, naming the
 * document and the person. So the app keeps its own bilateral relationship
 * registry here and, once a link is two-sided, SHARES its own protected
 * documents with that neighbour. The app carries no access CHECK (that stays the
 * SDK's job — see knowledge_trust-model); it only shares what follows from its
 * own relationship graph, and it never handles a key or an inbox address.
 *
 * A link between `a` and `b` is live only when BOTH `a → b` and `b → a` have been
 * asserted. A reader who unilaterally self-declares a link to an owner gets
 * nothing: the owner never asserted them back, so nothing is shared with them.
 */

import { inbox } from '@ng-eventually/polyfill';
import { listMyEntityDocs } from './storeRegistry';

/** Accumulates directed assertions and exposes the bilateral neighbourhood. */
class RelationshipRegistry {
  /** identity id → the set of ids it has asserted a link TO. */
  private asserted = new Map<string, Set<string>>();

  /** Record that `from` asserts a link to `to` (one direction only). */
  assert(from: string, to: string): void {
    if (!from || !to || from === to) return;
    let s = this.asserted.get(from);
    if (!s) this.asserted.set(from, (s = new Set()));
    s.add(to);
  }

  /** Has `from` asserted a link to `to` (one direction)? */
  private hasAsserted(from: string, to: string): boolean {
    return this.asserted.get(from)?.has(to) ?? false;
  }

  /** The bilateral neighbours of `id`: every `q` that `id` and `q` each asserted. */
  neighbors(id: string): Set<string> {
    const out = new Set<string>();
    for (const to of this.asserted.get(id) ?? []) {
      if (this.hasAsserted(to, id)) out.add(to);
    }
    return out;
  }

  /** Every id that has asserted at least one link. */
  asserters(): Iterable<string> {
    return this.asserted.keys();
  }

  clear(): void {
    this.asserted.clear();
  }
}

const registry = new RelationshipRegistry();

/**
 * Declare the connections a session asserts, as `self`, to each id in `peers`,
 * then share what follows. For every bilateral link (both sides asserted), the
 * session shares each of ITS OWN protected documents with that neighbour —
 * `inbox.share(doc, neighbour)`, one act naming the document and the person.
 * Re-callable whenever the relationship graph changes.
 *
 * `self` is the id of the asserting identity; a session only ever asserts — and
 * only ever shares — its own side. Sharing requires holding the document, and a
 * session holds only what it created.
 *
 * A NEIGHBOUR MUST BE SOMEONE WHO EXISTS: `inbox.share` names a person, and it
 * refuses a recipient nobody has signed in as rather than creating them. So the
 * keys handed here must come from the relationship graph of REAL accounts — a
 * key derived from a profile that is only a fixture names nobody, and the share
 * rejects. Nothing downstream waits on this call (the caller is
 * fire-and-forget), so such a rejection surfaces as a logged error, never as a
 * silent grant.
 *
 * Idempotent in EFFECT, not in traffic: the recipient applies a key it already
 * holds to no effect, so re-sharing changes nothing for them — but each call is
 * a fresh deposit (see the caller's note).
 */
export async function declareConnections(peers: Iterable<string>, self: string): Promise<void> {
  if (!self) return;
  for (const peer of peers) registry.assert(self, peer);

  const neighbours = registry.neighbors(self);
  if (neighbours.size === 0) return;
  // My own protected documents. Resolved once: the set is the same for every
  // neighbour, and each resolution is a broker read.
  const myDocs = await listMyEntityDocs('protected');
  if (myDocs.length === 0) return;
  for (const neighbour of neighbours) {
    for (const doc of myDocs) {
      await inbox.share(doc, neighbour);
    }
  }
}

/** Reset the relationship registry (mainly for tests / fresh sessions). */
export function resetConnections(): void {
  registry.clear();
}
